import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';
import { useBusinessAccountContext } from '@/hooks/useBusinessAccountContext';
import { cashfreeApi } from '@/lib/api/services/cashfree';
import { Loader2, PlugZap, Unplug, ArrowLeft, CheckCircle, XCircle, CreditCard, ExternalLink, Copy, Clock, RefreshCw, Ban } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { motion } from 'framer-motion';
import DataTable, { Column } from '@/components/reusable/DataTable';
import { ContactsPagination } from '@/components/contacts/ContactsPagination';
import { SearchBar } from '@/components/reusable/SearchBar';
import { DisconnectConfirmDialog } from '@/components/integrations/DisconnectConfirmDialog';

type PaymentLink = {
  id: string;
  cashfree_link_id: string;
  short_url: string;
  amount: number;
  currency: string;
  description?: string;
  status: string;
  customer_name?: string;
  customer_email?: string;
  customer_contact?: string;
  created_at: string;
  updated_at?: string;
};

const CashfreePage: React.FC = () => {
  const { selectedAccountId, isLoading: accountsLoading } = useBusinessAccountContext();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Connection state
  const [form, setForm] = useState({ appId: '', secretKey: '', accountName: '', webhookSecret: '', environment: 'sandbox' as 'sandbox' | 'production' });
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [testing, setTesting] = useState(false);
  // Init true when a BA is already selected (checkConnection runs on mount and
  // clears it) so the first paint shows a spinner, not a one-frame "connect"
  // form flash. False when no BA → checkConnection doesn't run, no stuck spinner.
  const [loadingData, setLoadingData] = useState(!!selectedAccountId);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionInfo, setConnectionInfo] = useState<{
    environment?: string;
    accountName?: string;
    connectedAt?: string;
  } | null>(null);

  // Payment links state
  const [paymentLinks, setPaymentLinks] = useState<PaymentLink[]>([]);
  const [loadingLinks, setLoadingLinks] = useState(false);
  const [linksPage, setLinksPage] = useState(1);
  const [linksPageSize, setLinksPageSize] = useState(10);
  const [linksTotal, setLinksTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');


  const rawTab = searchParams.get('tab') || 'connect';
  const activeTab = (!isConnected && !loadingData && !accountsLoading) ? 'connect' : rawTab;
  const setActiveTab = (tab: string) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.set('tab', tab);
      return newParams;
    });
  };

  const resetState = useCallback(() => {
    setIsConnected(false);
    setConnectionInfo(null);
    setPaymentLinks([]);
    setLinksTotal(0);
  }, []);

  const checkConnection = useCallback(async () => {
    if (!selectedAccountId) return;
    setLoadingData(true);
    try {
      const result = await cashfreeApi.checkConnection(selectedAccountId);
      if (result.connected) {
        setIsConnected(true);
        setConnectionInfo({
          environment: result.environment,
          accountName: result.accountName,
          connectedAt: result.connectedAt,
        });
      } else {
        resetState();
      }
    } catch {
      resetState();
    } finally {
      setLoadingData(false);
    }
  }, [selectedAccountId, resetState]);

  useEffect(() => {
    if (selectedAccountId) {
      checkConnection();
    } else {
      resetState();
    }
  }, [selectedAccountId, checkConnection, resetState]);

  // Load payment links when tab changes, pagination, or status filter changes
  useEffect(() => {
    if (selectedAccountId && isConnected && activeTab === 'links') {
      loadPaymentLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId, activeTab, linksPage, linksPageSize, statusFilter, isConnected]);

  // Debounced search
  useEffect(() => {
    if (!selectedAccountId || activeTab !== 'links') return;
    const timeoutId = setTimeout(() => {
      loadPaymentLinks();
    }, 300);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const loadPaymentLinks = async () => {
    if (!selectedAccountId) return;
    setLoadingLinks(true);
    try {
      const result = await cashfreeApi.listPaymentLinks(selectedAccountId, {
        page: linksPage,
        limit: linksPageSize,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: searchTerm || undefined,
      });
      setPaymentLinks(result.items || []);
      setLinksTotal(result.total || 0);
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to load payment links' } });
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleConnect = async () => {
    if (!selectedAccountId || !form.appId || !form.secretKey) return;
    setConnecting(true);
    try {
      await cashfreeApi.connect({
        businessId: selectedAccountId,
        appId: form.appId,
        secretKey: form.secretKey,
        accountName: form.accountName || undefined,
        webhookSecret: form.webhookSecret || undefined,
        environment: form.environment,
      });
      toast({ variant: "success", title: 'Connected', description: 'Cashfree connected successfully' });
      setForm({ appId: '', secretKey: '', accountName: '', webhookSecret: '', environment: 'sandbox' });
      checkConnection();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to connect Cashfree' } });
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!selectedAccountId) return;
    setDisconnecting(true);
    try {
      await cashfreeApi.disconnect(selectedAccountId);
      toast({ variant: "success", title: 'Disconnected', description: 'Cashfree disconnected' });
      setDisconnectDialogOpen(false);
      resetState();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to disconnect Cashfree' } });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    if (!selectedAccountId) return;
    setTesting(true);
    try {
      const result = await cashfreeApi.testConnection(selectedAccountId);
      toast({
        title: result.valid ? 'Connection Valid' : 'Connection Failed',
        description: result.valid ? 'Cashfree credentials are working' : (result.error || 'Test failed'),
        variant: result.valid ? 'success' : 'destructive',
      });
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to test connection' } });
    } finally {
      setTesting(false);
    }
  };

  const handleCancelPaymentLink = async (linkId: string) => {
    if (!selectedAccountId) return;
    try {
      await cashfreeApi.cancelPaymentLink(selectedAccountId, linkId);
      toast({ variant: "success", title: 'Success', description: 'Payment link cancelled' });
      await loadPaymentLinks();
    } catch (error: any) {
      showApiError(error, { fallback: { title: 'Failed to cancel payment link' } });
    }
  };

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    toast({ variant: "success", title: 'Copied', description: 'Payment link copied to clipboard' });
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; label: string; icon: any; style?: React.CSSProperties }> = {
      created: { variant: 'outline', label: 'Created', icon: Clock },
      active: { variant: 'default', label: 'Active', icon: CheckCircle },
      paid: { variant: 'default', label: 'Paid', icon: CheckCircle, style: { backgroundColor: 'var(--secondary-color)', color: '#fff' } },
      expired: { variant: 'destructive', label: 'Expired', icon: XCircle },
      cancelled: { variant: 'destructive', label: 'Cancelled', icon: XCircle },
      partially_paid: { variant: 'secondary', label: 'Partial', icon: Clock },
    };
    const statusInfo = statusMap[status?.toLowerCase()] || { variant: 'outline' as const, label: status || 'Unknown', icon: Clock };
    const Icon = statusInfo.icon;
    return (
      <Badge variant={statusInfo.variant} className="flex items-center w-fit gap-1" style={statusInfo.style}>
        <Icon className="h-3 w-3" />
        {statusInfo.label}
      </Badge>
    );
  };

  const linksTotalPages = Math.ceil(linksTotal / linksPageSize);

  const paymentLinksColumns: Column<PaymentLink>[] = useMemo(() => [
    {
      key: 'short_url',
      header: 'Payment Link',
      render: (_: any, link: PaymentLink) => (
        <div className="flex items-center gap-2">
          {link.short_url ? (
            <>
              <a href={link.short_url} target="_blank" rel="noopener noreferrer"
                className="text-primary hover:underline text-sm font-medium flex items-center gap-1.5 transition-colors">
                {link.short_url.length > 40 ? link.short_url.substring(0, 40) + '...' : link.short_url}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:bg-muted"
                onClick={() => handleCopyLink(link.short_url)} title="Copy link">
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </>
          ) : (
            <span className="text-muted-foreground text-sm">N/A</span>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      render: (_: any, link: PaymentLink) => (
        <div className="font-semibold text-base">
          {new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: link.currency || 'INR',
          }).format(Number(link.amount) || 0)}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_: any, link: PaymentLink) => getStatusBadge(link.status),
    },
    {
      key: 'description',
      header: 'Description',
      render: (_: any, link: PaymentLink) => (
        <span className="text-sm text-muted-foreground line-clamp-1 max-w-[200px]">
          {link.description || 'N/A'}
        </span>
      ),
    },
    {
      key: 'customer_name',
      header: 'Customer',
      render: (_: any, link: PaymentLink) => (
        <div className="text-sm">
          {link.customer_name || link.customer_contact || link.customer_email || <span className="text-muted-foreground">N/A</span>}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      render: (_: any, link: PaymentLink) => (
        <span className="text-sm text-muted-foreground">
          {link.created_at ? new Date(link.created_at).toLocaleString('en-IN', {
            dateStyle: 'medium',
            timeStyle: 'short',
          }) : 'N/A'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (_: any, link: PaymentLink) => (
        <div className="flex items-center justify-end gap-1">
          {['created', 'active'].includes(link.status?.toLowerCase()) && (
            <button
              className="inline-flex items-center h-6 px-2 rounded text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors"
              onClick={() => handleCancelPaymentLink(link.cashfree_link_id || link.id)}
            >
              <Ban className="h-3 w-3 mr-1" />
              Cancel
            </button>
          )}
        </div>
      ),
    },
  ], []);

  if (accountsLoading || loadingData) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/home/integrations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <CardTitle className="text-xl sm:text-2xl">Cashfree Integration</CardTitle>
          <CardDescription className="text-sm">Connect your Cashfree account to process payments</CardDescription>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className='inline-flex gap-2 p-2 h-[46px] rounded-[20px]'>
          <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="connect">
            <span className="relative z-10">Connect</span>
            {activeTab === 'connect' && (
              <motion.div layoutId="cashfree-tab-pill" className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                transition={{ type: "spring", bounce: 0.2, duration: 0.5 }} />
            )}
          </TabsTrigger>
          <TabsTrigger className='relative rounded-[12px] data-[state=active]:bg-transparent data-[state=active]:shadow-none' value="links"
            disabled={!isConnected}>
            <span className="relative z-10">Payment Links</span>
            {activeTab === 'links' && (
              <motion.div layoutId="cashfree-tab-pill" className="absolute inset-0 bg-background rounded-[12px] shadow-sm z-0"
                transition={{ type: "spring", bounce: 0.2, duration: 0.5 }} />
            )}
          </TabsTrigger>
        </TabsList>

        {/* Connection Tab */}
        <TabsContent value="connect">
          <Card className="border-none">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{isConnected ? 'Connection Status' : 'Connect Cashfree'}</CardTitle>
                  <CardDescription>
                    {isConnected ? 'Current Cashfree connection status' : 'Enter your Cashfree API credentials from merchant dashboard'}
                  </CardDescription>
                </div>
                {isConnected && (
                  <Button variant="outline" size="sm" onClick={checkConnection} disabled={loadingData}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingData ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isConnected ? (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-sm font-medium">Connected</span>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Environment</Label>
                      <p className="font-medium capitalize">{connectionInfo?.environment || 'sandbox'}</p>
                    </div>
                    {connectionInfo?.accountName && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Account Name</Label>
                        <p className="font-medium">{connectionInfo.accountName}</p>
                      </div>
                    )}
                    {connectionInfo?.connectedAt && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Connected At</Label>
                        <p className="font-medium">{new Date(connectionInfo.connectedAt).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={handleTestConnection} disabled={testing}>
                      {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlugZap className="h-4 w-4 mr-2" />}
                      Test Connection
                    </Button>
                    <Button variant="destructive" onClick={() => setDisconnectDialogOpen(true)} disabled={disconnecting}>
                      {disconnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unplug className="h-4 w-4 mr-2" />}
                      Disconnect
                    </Button>
                  </div>

                  <DisconnectConfirmDialog
                    open={disconnectDialogOpen}
                    onOpenChange={setDisconnectDialogOpen}
                    onConfirm={handleDisconnect}
                    isLoading={disconnecting}
                    integration="cashfree"
                  />
                </motion.div>
              ) : (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="appId">App ID *</Label>
                    <Input id="appId" placeholder="Enter your Cashfree App ID" value={form.appId}
                      onChange={(e) => setForm(prev => ({ ...prev, appId: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="secretKey">Secret Key *</Label>
                    <Input id="secretKey" type="password" placeholder="Enter your Cashfree Secret Key" value={form.secretKey}
                      onChange={(e) => setForm(prev => ({ ...prev, secretKey: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="accountName">Account Name (optional)</Label>
                    <Input id="accountName" placeholder="A friendly name for this connection" value={form.accountName}
                      onChange={(e) => setForm(prev => ({ ...prev, accountName: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="webhookSecret">Webhook Secret (optional)</Label>
                    <Input id="webhookSecret" type="password" placeholder="For webhook signature verification" value={form.webhookSecret}
                      onChange={(e) => setForm(prev => ({ ...prev, webhookSecret: e.target.value }))} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="environment">Environment</Label>
                    <Select value={form.environment} onValueChange={(val) => setForm(prev => ({ ...prev, environment: val as 'sandbox' | 'production' }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sandbox">Sandbox (Testing)</SelectItem>
                        <SelectItem value="production">Production (Live)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleConnect} disabled={connecting || !form.appId || !form.secretKey} className="w-full">
                    {connecting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Connecting...</> : <><PlugZap className="h-4 w-4 mr-2" />Connect Cashfree</>}
                  </Button>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Links Tab */}
        <TabsContent value="links" className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className='text-lg sm:text-xl md:text-2xl font-bold'>Payment Links</CardTitle>
              <CardDescription className='text-xs sm:text-sm md:text-base text-gray-600'>Payment links created via Cashfree</CardDescription>
            </div>
          </div>
          <div className='flex justify-between flex-wrap gap-4'>
            <SearchBar
              placeholder="Search by Link ID, Description, Customer..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setLinksPage(1);
              }}
              onClear={() => {
                setSearchTerm('');
                setLinksPage(1);
              }}
              containerClassName="max-w-sm"
            />
            <Select value={statusFilter} onValueChange={(value) => {
              setStatusFilter(value);
              setLinksPage(1);
            }}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card className='rounded-[12px] border-0 shadow-[0px_0px_10px_0px_rgba(0,0,0,0.06)]'>
            <CardContent>
              <div className="space-y-4">
                {loadingLinks ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                  </div>
                ) : paymentLinks.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <CreditCard className="h-12 w-12 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No payment links found</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {searchTerm || statusFilter !== 'all'
                        ? 'Try adjusting your search or filters'
                        : 'Payment links created from inbox will appear here'}
                    </p>
                  </div>
                ) : (
                  <>
                    <DataTable
                      data={paymentLinks}
                      columns={paymentLinksColumns}
                      showPagination={false}
                      innerScroll={false}
                    />
                    {linksTotalPages > 1 && (
                      <ContactsPagination
                        currentPage={linksPage}
                        totalPages={linksTotalPages}
                        totalCount={linksTotal}
                        pageSize={linksPageSize}
                        onPageChange={setLinksPage}
                        onPageSizeChange={(size) => { setLinksPageSize(size); setLinksPage(1); }}
                        isLoading={loadingLinks}
                        itemLabel="payment links"
                        pageSizeOptions={[10, 20]}
                        minPageSize={10}
                      />
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

    </div>
  );
};

export default CashfreePage;
